import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { addMultipleNewUser, fetchDepartmentsPaginated } from 'services/registration';
import { APIErrors } from 'types/api';
import { Workplace } from 'views/employee-management/useEmployees';
import { AddMultipleNewUserRequest, AddMultipleNewUserValue } from '.';
import { schema } from './validation';

export const useAddMultipleNewUserForm = ({ adminWorkplaces, onCancel }: { adminWorkplaces: Workplace[]; onCancel: (boolean) => void }) => {
    const { t } = useTranslation();

    const form = useForm<AddMultipleNewUserValue>({
        resolver: yupResolver(schema),
        defaultValues: {
            count: null,
            workplace_id: adminWorkplaces[0]?.id.toString() || '',
            department_id: '',
        },
        mode: 'onChange',
    });

    const { watch, trigger, setValue, setError } = form;

    const workplaceId = Number(watch('workplace_id'));
    const countValue = watch('count');

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);

    useEffect(() => {
        if (countValue !== null) {
            trigger('count');
        }
    }, [countValue, trigger]);

    useEffect(() => {
        setSelectedDepartment(null);
        setValue('department_id', '');
    }, [workplaceId, setValue]);

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await fetchDepartmentsPaginated(workplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleDepartmentChange = (
        option: { label: string; value: number } | null,
        fieldOnChange: (val: number | string | null) => void
    ) => {
        setSelectedDepartment(option);
        fieldOnChange(option?.value ?? '');
    };

    const mutation = useMutation<AxiosResponse<Blob>, AxiosError<APIErrors>, AddMultipleNewUserRequest>(
        (payload) => addMultipleNewUser(payload),
        {
            onSuccess: (data) => {
                const url = window.URL.createObjectURL(data.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'login-and-password.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                toast.success(t('employeeManagement.generateLoginAndPasswordSuccessMessage'));
                onCancel(true);
            },
            onError: async (error) => {
                const data = error?.response?.data;
                let errors: Record<string, string[]> | undefined;
                if (data instanceof Blob) {
                    try {
                        const text = await data.text();
                        errors = JSON.parse(text)?.errors;
                    } catch (e) {
                        // response body is not valid JSON, ignore
                    }
                } else {
                    errors = (data as any)?.errors;
                }
                if (errors) {
                    Object.entries(errors).forEach(([key, value]) => {
                        setError(key as keyof AddMultipleNewUserValue, {
                            message: value?.[0],
                        });
                    });
                }
            },
        }
    );

    const onSubmit = (data: AddMultipleNewUserValue) => {
        mutation.mutate({ data });
    };

    return {
        t,
        form,
        workplaceId,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        onSubmit,
        isSubmitting: mutation.isLoading,
    };
};
